import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Landmark, Loader, CheckCircle, Info, Zap } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../../components/ui/Dialog';
import { OrderDetail } from '../../services/order';

/*
 * Set these in your .env (Vite):
 *   VITE_SETTLEMENT_API_URL=https://prod-native-api.cleverpe.com/api/settlement
 *   VITE_SETTLEMENT_API_KEY=<x-api-key>
 * (CRA: use process.env.REACT_APP_* instead of import.meta.env)
 */
const SETTLEMENT_API_URL: string =
  import.meta.env.VITE_SETTLEMENT_API_URL ??
  'https://prod-native-api.cleverpe.com/api/settlement';

const SETTLEMENT_API_KEY: string =
  import.meta.env.VITE_SETTLEMENT_API_KEY ?? '';

/* Hardcoded values, same as the backend flow today */
const SURL = 'https://pay.easebuzz.dev/seamless/response';
const FURL = 'https://pay.easebuzz.dev/seamless/response';
const REQUEST_FLOW = 'SEAMLESS';
const PAYMENT_MODE = 'EMI PFT';
const BANK_CODE = 'GFINPFT';
// const SPLIT_PAYMENTS = { Cleverpe: '200.0' };

type SettlementStatus = 'success' | 'failure' | 'pending';

interface PASettlementTabProps {
  order: OrderDetail;
  canEdit: boolean;
  /** Re-fetch the order so the new settlement fields show up */
  onRefresh?: () => Promise<void>;
}

const postSettlement = async (path: string, body: unknown) => {
  const response = await fetch(`${SETTLEMENT_API_URL}/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': SETTLEMENT_API_KEY,
    },
    body: JSON.stringify(body),
  });

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    /* non-JSON response */
  }

  if (!response.ok) {
    throw new Error(
      data?.message || data?.error || `Request failed (${response.status})`
    );
  }

  return data;
};

const STATUS_BADGE: Record<
  SettlementStatus | 'none',
  { label: string; className: string }
> = {
  none: { label: 'Not Initiated', className: 'bg-gray-100 text-gray-700' },
  pending: { label: 'Settlement Initiated', className: 'bg-amber-100 text-amber-800' },
  success: { label: 'Settlement Successful', className: 'bg-green-100 text-green-800' },
  failure: { label: 'Settlement Failed', className: 'bg-red-100 text-red-800' },
};

const formatAmount = (value: unknown) => {
  const amount = Number(value);
  if (value == null || value === '' || !Number.isFinite(amount)) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount);
};

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-xs font-medium uppercase text-gray-500 tracking-wider mb-1">
      {label}
    </p>
    <p className="font-medium break-all">{value || 'N/A'}</p>
  </div>
);

export const PASettlementTab: React.FC<PASettlementTabProps> = ({
  order,
  canEdit,
  onRefresh,
}) => {
  // Step 1: register settlement
  const [isRegistering, setIsRegistering] = useState(false);

  // Step 2: initiate settlement (confirms the txn as success)
  const [isInitiating, setIsInitiating] = useState(false);
  const [confirmInitiateOpen, setConfirmInitiateOpen] = useState(false);

  // Step 3: on-demand settlement
  const [isRequestingOnDemand, setIsRequestingOnDemand] = useState(false);
  const [confirmOnDemandOpen, setConfirmOnDemandOpen] = useState(false);
  const [onDemandRequested, setOnDemandRequested] = useState(false);

  // Settlement fields written by the settlement initiate API
  const s = (order.settlement ?? {}) as Record<string, any>;

  const status = s.settlementStatus as SettlementStatus | undefined;
  const hasSettlement = Boolean(status);
  const badge = STATUS_BADGE[status ?? 'none'];

  /* ---------------------------------------------------------
   * STEP 1: REGISTER SETTLEMENT  (calls /initiate)
   * --------------------------------------------------------- */
  const handleRegister = async () => {
    const kfs = order.keyFactStatement as any;

    const netLoanAmount =
      kfs?.calculations?.netLoanAmount ?? kfs?.netLoanAmount;

    const cleverPeShare =
      kfs?.calculations?.cleverPeShare ?? kfs?.cleverPeShare;

    const email = String(s.customerEmail ?? '').trim();
    const firstname = String(s.customerName ?? '').trim();
    const phone = String(s.customerMobileNo ?? '').trim();
    const productinfo = String(
      (order as any).productDetails ?? order.product?.name ?? ''
    ).trim();

    const missing: string[] = [];
    if (!Number.isFinite(Number(netLoanAmount)) || Number(netLoanAmount) <= 0) {
      missing.push('net loan amount');
    }
    if (!email) missing.push('customer email');
    if (!firstname) missing.push('customer name');
    if (!phone) missing.push('customer mobile');
    if (!productinfo) missing.push('product details');

    if (missing.length > 0) {
      toast.error(`Cannot register settlement. Missing: ${missing.join(', ')}.`);
      return;
    }

    setIsRegistering(true);

    try {
      await postSettlement('initiate', {
        orderId: order.orderId,
        order_info: {
          amount: Number(netLoanAmount).toFixed(2),
          txnid: `STL-${order.orderId}`,
          email,
          productinfo,
          firstname,
          phone,
          surl: SURL,
          furl: FURL,
          request_flow: REQUEST_FLOW,
        },
        payment_info: {
          payment_mode: PAYMENT_MODE,
          bank_code: BANK_CODE,
        },
        split_payments: { Cleverpe: cleverPeShare },
      });

      toast.success('Settlement registered');
      await onRefresh?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to register settlement'
      );
    } finally {
      setIsRegistering(false);
    }
  };

  /* ---------------------------------------------------------
   * STEP 2: INITIATE SETTLEMENT  (calls /status-update, txn = success)
   * --------------------------------------------------------- */
  const handleInitiateSettlement = async () => {
    const txnid = String(s.settlementTxnid ?? '').trim();
    const easepayid = String(s.settlementEasepayId ?? '').trim();
    const amount = Number(s.settlementAmount);

    if (!txnid || !easepayid || !Number.isFinite(amount) || amount <= 0) {
      setConfirmInitiateOpen(false);
      toast.error(
        'Settlement transaction details are missing. Register settlement first.'
      );
      return;
    }

    setIsInitiating(true);

    try {
      await postSettlement('status-update', {
        orderId: order.orderId,
        order_info: {
          txnid,
          easepayid,
          txn_status: 'success',
          amount: amount.toFixed(2),
        },
      });

      toast.success('Settlement initiated');
      setConfirmInitiateOpen(false);
      await onRefresh?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to initiate settlement'
      );
    } finally {
      setIsInitiating(false);
    }
  };

  /* ---------------------------------------------------------
   * STEP 3: REQUEST ON-DEMAND SETTLEMENT  (calls /ondemand-initiate)
   * --------------------------------------------------------- */
  const handleRequestOnDemand = async () => {
    setIsRequestingOnDemand(true);

    try {
      await postSettlement('ondemand-initiate', {
        orderId: order.orderId,
      });

      toast.success('On-demand settlement requested');
      setConfirmOnDemandOpen(false);
      setOnDemandRequested(true);
      await onRefresh?.();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to request on-demand settlement'
      );
    } finally {
      setIsRequestingOnDemand(false);
    }
  };

  /* Which action buttons to show */
  const showRegister = !hasSettlement;
  const showInitiate = hasSettlement && status !== 'success';
  const showOnDemand = status === 'success' && !onDemandRequested;

  return (
    <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center">
          <div className="bg-emerald-100 p-2 rounded-full mr-3">
            <Landmark className="text-emerald-600" size={18} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">PA Settlement</h3>
        </div>

        <span
          className={`px-2 py-1 rounded-full text-sm font-medium ${badge.className}`}
        >
          {badge.label}
        </span>
      </div>

      {/* Body */}
      {hasSettlement ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-5 gap-x-6">
          <Field label="Settlement Txn ID" value={s.settlementTxnid} />
          <Field label="Easepay ID" value={s.settlementEasepayId} />
          <Field label="Mode" value={s.settlementMode} />
          <Field label="Amount" value={formatAmount(s.settlementAmount)} />
          <Field label="Initiated On" value={s.settlementAddedOn} />
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <Info size={24} className="text-gray-400" />
          </div>
          <p className="text-gray-800 font-medium mb-1">
            Settlement not registered
          </p>
          <p className="text-gray-500 text-sm text-center max-w-md">
            No settlement data is available for this order yet.
          </p>
        </div>
      )}

      {/* Actions: only the button for the current step is shown */}
      {canEdit && (showRegister || showInitiate || showOnDemand) && (
        <div className="flex flex-wrap justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          {showRegister && (
            <Button
              onClick={handleRegister}
              disabled={isRegistering}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {isRegistering ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Registering...
                </>
              ) : (
                'Register Settlement'
              )}
            </Button>
          )}

          {showInitiate && (
            <Button
              variant="outline"
              onClick={() => setConfirmInitiateOpen(true)}
              disabled={isInitiating}
              className="flex items-center gap-2"
            >
              <CheckCircle size={16} className="text-green-600" />
              Initiate Settlement
            </Button>
          )}

          {showOnDemand && (
            <Button
              onClick={() => setConfirmOnDemandOpen(true)}
              disabled={isRequestingOnDemand}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              <Zap size={16} />
              Request On-Demand Settlement
            </Button>
          )}
        </div>
      )}

      {/* Confirm: initiate settlement */}
      <Dialog
        isOpen={confirmInitiateOpen}
        onClose={() => !isInitiating && setConfirmInitiateOpen(false)}
        title="Initiate settlement?"
        size="sm"
      >
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            This tells Easebuzz that the settlement transaction{' '}
            <span className="font-medium">{s.settlementTxnid || 'N/A'}</span>{' '}
            was successful. It cannot be undone from here.
          </p>

          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmInitiateOpen(false)}
              disabled={isInitiating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInitiateSettlement}
              disabled={isInitiating}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
            >
              {isInitiating ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Initiating...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Confirm: request on-demand settlement */}
      <Dialog
        isOpen={confirmOnDemandOpen}
        onClose={() => !isRequestingOnDemand && setConfirmOnDemandOpen(false)}
        title="Request on-demand settlement?"
        size="sm"
      >
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            This sends an on-demand settlement request to Easebuzz for order{' '}
            <span className="font-medium">{order.orderId}</span>. It cannot be
            undone from here.
          </p>

          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOnDemandOpen(false)}
              disabled={isRequestingOnDemand}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRequestOnDemand}
              disabled={isRequestingOnDemand}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {isRequestingOnDemand ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Requesting...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PASettlementTab;